import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  Description as DocumentIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { formatDate, formatDateTime } from '../../utils/dateFormat';

const STATUS_LABELS = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed'
};

const STATUS_COLORS = {
  PENDING: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'success'
};

const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FS = '0.8125rem';

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: FS, fontWeight: 500 }}>{value || '—'}</Typography>
  </Grid>
);

const PortalTaskDetail = ({ open, taskId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [task, setTask] = useState(null);

  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchTask = useCallback(async () => {
    if (!open || !taskId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getPortalTask(taskId);
      setTask(response.data?.data || null);
    } catch (err) {
      setTask(null);
      setError('Unable to load this task.');
    } finally {
      setLoading(false);
    }
  }, [open, taskId]);

  const fetchDocuments = useCallback(async () => {
    if (!open || !taskId) return;
    setDocsLoading(true);
    setDocsError(null);
    try {
      const response = await ApiService.getPortalTaskDocuments(taskId);
      setDocuments(response.data?.data || []);
    } catch (err) {
      setDocuments([]);
      setDocsError('Unable to load documents for this task.');
    } finally {
      setDocsLoading(false);
    }
  }, [open, taskId]);

  useEffect(() => { fetchTask(); }, [fetchTask]);
  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDownload = async (documentId) => {
    setDownloadingId(documentId);
    setDocsError(null);
    try {
      const response = await ApiService.getPortalTaskDocumentDownloadUrl(taskId, documentId);
      const url = response.data?.data?.download_url || response.data?.data?.downloadUrl;
      if (!url) throw new Error('No download url');
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setDocsError('Unable to download this document.');
    } finally {
      setDownloadingId(null);
    }
  };

  const documentMeta = (doc) => [
    doc.document_type,
    formatDateTime(doc.uploaded_at, null),
    formatFileSize(doc.file_size)
  ].filter(Boolean).join(' · ');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>Task Details</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !task ? (
          <Typography sx={{ fontSize: FS, color: 'text.secondary' }}>No task details available.</Typography>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {task.task_name || '—'}
              </Typography>
              <Chip
                size="small"
                label={STATUS_LABELS[task.status] || task.status}
                color={STATUS_COLORS[task.status] || 'default'}
              />
            </Box>

            <Typography sx={{ fontSize: FS, color: 'text.secondary', mb: 1.5 }}>
              {task.description || 'No description provided.'}
            </Typography>

            <Divider sx={{ mb: 1.5 }} />

            <Grid container spacing={1.5}>
              <Field label="Task #" value={task.task_number} />
              <Field label="Work Order Number" value={task.work_order_number} />
              <Field label="Work Order Status" value={task.work_order_status} />
              <Field label="Employee" value={task.customer_name} />
              <Field label="Service" value={task.service_name} />
              <Field label="Created" value={formatDateTime(task.created_at, '—')} />
              <Field label="Last Updated" value={formatDateTime(task.updated_at, '—')} />
              <Field label="Completed" value={formatDate(task.completed_at, '—')} />
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            <Typography sx={{ fontSize: FS, fontWeight: 600, mb: 0.5 }}>
              Documents
            </Typography>

            {docsError && <Alert severity="error" sx={{ mb: 1 }}>{docsError}</Alert>}

            {docsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : documents.length === 0 ? (
              <Typography sx={{ fontSize: FS, color: 'text.secondary' }}>
                No documents linked to this task.
              </Typography>
            ) : (
              <List dense disablePadding>
                {documents.map((doc) => (
                  <ListItem
                    key={doc.id}
                    disableGutters
                    secondaryAction={
                      <Tooltip title="Download">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(doc.id)}
                            disabled={downloadingId === doc.id}
                          >
                            {downloadingId === doc.id
                              ? <CircularProgress size={18} />
                              : <DownloadIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <DocumentIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={doc.file_name || '—'}
                      secondary={documentMeta(doc) || null}
                      primaryTypographyProps={{ fontSize: FS }}
                      secondaryTypographyProps={{ fontSize: '0.6875rem' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontSize: FS }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PortalTaskDetail;
